import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, Sparkles, Clock, FileText, Send } from "lucide-react";
import { TRADES, TRADE_GENERIC } from "@/data/trades";
import { LegalLinks } from "@/components/LegalLinks";

export default function TradeLanding({ slug: forcedSlug }) {
  const params = useParams();
  const slug = forcedSlug || params.slug;
  const trade = slug && slug !== "general" ? TRADES[slug] : TRADE_GENERIC;

  useEffect(() => {
    if (!trade) return;
    document.title = trade.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.setAttribute("name", "description");
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", trade.meta);
  }, [trade]);

  if (!trade) return <Navigate to="/" replace />;

  const otherTrades = Object.values(TRADES).filter((t) => t.slug !== slug);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid={`trade-landing-${trade.slug}`}>
      {/* Nav */}
      <header className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-extrabold text-xl tracking-tight">AutoPresupuestos<span className="text-orange-600">.</span></Link>
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/login" className="px-4 h-10 inline-flex items-center hover:text-orange-600">Iniciar sesión</Link>
            <Link to="/register" className="px-5 h-10 inline-flex items-center btn-brand" data-testid="trade-cta-top">Empezar gratis</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 py-20 sm:py-28">
          <Link to="/" className="kbar inline-flex items-center hover:text-orange-600">← Para todos los oficios</Link>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 max-w-4xl">
            {trade.h1}
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-3xl">{trade.intro}</p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link to="/register" data-testid="trade-cta-hero" className="btn-brand h-14 px-8 inline-flex items-center gap-2 text-base">
              Empieza gratis 14 días <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/login" className="h-14 px-6 inline-flex items-center text-slate-700 hover:text-slate-900 border border-slate-200 hover:border-slate-300">Ya tengo cuenta</Link>
          </div>
          <div className="mt-6 text-sm text-slate-500 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Sin tarjeta para empezar</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Desde 29 €/mes</span>
            <span className="flex items-center gap-2"><Check className="w-4 h-4 text-green-600" /> Cancela cuando quieras</span>
          </div>
        </div>
      </section>

      {/* Examples */}
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="kbar">Ejemplos de trabajos</div>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-2">Que ya lo está usando.</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-slate-200 border border-slate-200">
            {trade.examples.map((ex) => (
              <div key={ex} className="bg-white p-5">
                <Sparkles className="w-5 h-5 text-orange-600" />
                <div className="mt-3 text-sm font-semibold leading-snug">{ex}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="kbar">Por qué AutoPresupuestos Pro</div>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl mt-2 max-w-3xl">Pasa de Excel a IA en 5 minutos.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {trade.benefits.map((b) => (
              <div key={b.t} className="bg-white p-8">
                <Check className="w-6 h-6 text-orange-600" />
                <h3 className="mt-4 font-display font-bold text-xl">{b.t}</h3>
                <p className="mt-2 text-slate-600">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-b border-slate-200 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-5 py-16">
          <div className="kbar text-slate-400">Cómo funciona</div>
          <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl mt-2">3 pasos · 30 segundos · listo.</h2>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-700 border border-slate-700">
            {[
              { i: Sparkles, t: "Describe el trabajo", d: "Tipo, metros, horas, materiales, urgencia." },
              { i: FileText, t: "PDF profesional", d: "Con tu logo y color corporativo." },
              { i: Send, t: "Cliente firma online", d: "Email + WhatsApp con enlace para aceptar." },
            ].map((s) => (
              <div key={s.t} className="bg-slate-900 p-8">
                <s.i className="w-7 h-7 text-orange-500" />
                <h3 className="mt-4 font-display font-bold text-xl">{s.t}</h3>
                <p className="mt-2 text-slate-400">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      {trade.faq?.length > 0 && (
        <section className="border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-5 py-16">
            <div className="kbar">Preguntas frecuentes</div>
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-2">Lo que más se pregunta.</h2>
            <div className="mt-10 divide-y divide-slate-200 border-t border-slate-200">
              {trade.faq.map((f) => (
                <details key={f.q} className="py-6 group">
                  <summary className="cursor-pointer flex items-start justify-between gap-4 list-none">
                    <span className="font-display font-bold text-lg">{f.q}</span>
                    <span className="text-2xl text-orange-600 group-open:rotate-45 transition-transform">+</span>
                  </summary>
                  <p className="mt-3 text-slate-600">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="border-b border-slate-200 bg-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-5 py-20 grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-5xl">Empieza hoy. Tu primer presupuesto en 30 segundos.</h2>
            <p className="mt-4 text-white/90 text-lg">14 días gratis. Sin tarjeta. Sin permanencia.</p>
          </div>
          <Link to="/register" data-testid="trade-cta-final" className="bg-white text-orange-600 hover:bg-slate-50 h-14 px-8 inline-flex items-center justify-center gap-2 font-bold">
            Crear cuenta gratis <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* SEO interlinking — other trades */}
      <section className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-5 py-12">
          <div className="kbar">Para tu oficio</div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/presupuestos" className="px-4 h-10 border border-slate-200 hover:border-orange-600 hover:text-orange-600 text-sm font-semibold inline-flex items-center">Todos los oficios →</Link>
            {otherTrades.map((t) => (
              <Link key={t.slug} to={`/presupuestos-${t.slug}`} className="px-4 h-10 border border-slate-200 hover:border-orange-600 hover:text-orange-600 text-sm font-semibold inline-flex items-center">
                Presupuestos {t.name.toLowerCase()}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-8">
        <div className="max-w-7xl mx-auto px-5 text-sm text-slate-500 flex items-center justify-between flex-wrap gap-2">
          <span>© 2026 AutoPresupuestos Pro · Hecho para autónomos exigentes</span>
          <div className="flex gap-3">
            <Link to="/login" className="hover:text-slate-700">Iniciar sesión</Link>
            <Link to="/register" className="hover:text-slate-700">Crear cuenta</Link>
            <LegalLinks className="flex flex-wrap gap-3" itemClass="hover:text-slate-700" />
          </div>
        </div>
      </footer>
    </div>
  );
}
